import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, AlertCircle, CheckCircle2, Plus, ShieldAlert } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { securityIncidentApi, ApiError } from '@solara/api-client';
import type {
  SecurityIncidentDto,
  IncidentCategory,
  IncidentSeverity,
  IncidentStatsDto,
} from '@solara/api-client';

const SEVERITY_KEYS: readonly IncidentSeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CATEGORY_KEYS: readonly IncidentCategory[] = [
  'PHISHING',
  'CREDENTIAL_THEFT',
  'DATA_EXFILTRATION',
  'INSIDER_THREAT',
  'DDoS',
  'MALWARE',
  'MISCONFIGURATION',
  'OTHER',
];

const SEVERITY_TONES: Record<IncidentSeverity, 'neutral' | 'info' | 'warning' | 'danger'> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
};

interface DraftIncident {
  detectedAt: string;
  severity: IncidentSeverity;
  category: IncidentCategory;
  description: string;
  rootCause: string;
  mitigations: string;
}

function nowLocalIso(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

const EMPTY_DRAFT = (): DraftIncident => ({
  detectedAt: nowLocalIso(),
  severity: 'MEDIUM',
  category: 'PHISHING',
  description: '',
  rootCause: '',
  mitigations: '',
});

export default function SecurityIncidentsPage() {
  const { t } = useTranslation('p1');
  const [incidents, setIncidents] = useState<SecurityIncidentDto[]>([]);
  const [stats, setStats] = useState<IncidentStatsDto | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'OPEN' | 'RESOLVED'>('OPEN');
  const [severityFilter, setSeverityFilter] = useState<IncidentSeverity | 'ALL'>('ALL');
  const [draft, setDraft] = useState<DraftIncident | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const catLabel = (c: IncidentCategory | string) =>
    t(`incidents.categories.${c}`, { defaultValue: c });

  useEffect(() => {
    void load();
  }, [statusFilter, severityFilter]);

  async function load() {
    try {
      const params: { severity?: IncidentSeverity; status?: 'OPEN' | 'RESOLVED' } = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (severityFilter !== 'ALL') params.severity = severityFilter;
      const [list, s] = await Promise.all([
        securityIncidentApi.list(params),
        securityIncidentApi.stats(),
      ]);
      setIncidents(list);
      setStats(s);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function create() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await securityIncidentApi.create({
        detectedAt: new Date(draft.detectedAt).toISOString(),
        severity: draft.severity,
        category: draft.category,
        description: draft.description,
        rootCause: draft.rootCause || undefined,
        mitigations: draft.mitigations || undefined,
      });
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function resolve(inc: SecurityIncidentDto) {
    const rootCause = window.prompt(t('incidents.rootCausePrompt'), inc.rootCause ?? '');
    if (rootCause === null) return;
    const mitigations = window.prompt(t('incidents.mitigationsPrompt'), inc.mitigations ?? '');
    if (mitigations === null) return;
    setBusy(true);
    try {
      await securityIncidentApi.resolve(inc.id, {
        rootCause: rootCause || undefined,
        mitigations: mitigations || undefined,
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  const sevCounts = useMemo(() => stats?.bySeverity ?? null, [stats]);

  return (
    <div className="space-y-6 max-w-6xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('incidents.title')}</h1>
          <p className="text-fg-muted text-sm">{t('incidents.subtitle')}</p>
        </div>
        <Button onClick={() => setDraft(EMPTY_DRAFT())}>
          <Plus className="mr-2 h-4 w-4" />
          {t('incidents.logIncident')}
        </Button>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('incidents.openTotal')}</p>
            <p className="text-3xl font-semibold mt-2 text-danger">{stats?.open ?? 0}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('incidents.resolved')}</p>
            <p className="text-3xl font-semibold mt-2 text-success">{stats?.resolved ?? 0}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('incidents.avgMttr')}</p>
            <p className="text-3xl font-semibold mt-2">
              {stats?.avgMttrMinutes != null ? `${stats.avgMttrMinutes}m` : t('common.muted')}
            </p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('incidents.openHigh')}</p>
            <p className="text-3xl font-semibold mt-2 text-warning">
              {(sevCounts?.HIGH ?? 0) + (sevCounts?.CRITICAL ?? 0)}
            </p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('incidents.total')}</p>
            <p className="text-3xl font-semibold mt-2">{stats?.total ?? 0}</p>
          </CardBody>
        </Card>
      </div>

      {draft && (
        <Card className="rounded-2xl border-brand-500/30">
          <CardHeader>
            <CardTitle>{t('incidents.logTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label={t('common.detectedAt')}
                type="datetime-local"
                value={draft.detectedAt}
                onChange={(e) => setDraft({ ...draft, detectedAt: e.target.value })}
              />
              <label className="block text-sm">
                <span className="text-xs text-fg-muted">{t('common.severity')}</span>
                <select
                  className="w-full mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={draft.severity}
                  onChange={(e) =>
                    setDraft({ ...draft, severity: e.target.value as IncidentSeverity })
                  }
                >
                  {SEVERITY_KEYS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-xs text-fg-muted">{t('common.category')}</span>
                <select
                  className="w-full mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={draft.category}
                  onChange={(e) =>
                    setDraft({ ...draft, category: e.target.value as IncidentCategory })
                  }
                >
                  {CATEGORY_KEYS.map((c) => (
                    <option key={c} value={c}>
                      {catLabel(c)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-sm">
              <span className="text-xs text-fg-muted">{t('common.description')}</span>
              <textarea
                className="w-full min-h-[5rem] mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-xs text-fg-muted">{t('incidents.rootCauseOptional')}</span>
                <textarea
                  className="w-full min-h-[4rem] mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={draft.rootCause}
                  onChange={(e) => setDraft({ ...draft, rootCause: e.target.value })}
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs text-fg-muted">{t('incidents.mitigationsOptional')}</span>
                <textarea
                  className="w-full min-h-[4rem] mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={draft.mitigations}
                  onChange={(e) => setDraft({ ...draft, mitigations: e.target.value })}
                />
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={create} disabled={busy || draft.description.length < 5}>
                {busy ? t('actions.saving') : t('incidents.logIncident')}
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t('actions.cancel')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            {t('incidents.incidentsCount', { count: incidents.length })}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap gap-2 text-xs">
            <label className="flex items-center gap-1">
              <span className="text-fg-muted">{t('incidents.filterStatus')}</span>
              <select
                className="rounded border border-border bg-surface px-2 py-1"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              >
                <option value="OPEN">{t('common.open')}</option>
                <option value="RESOLVED">{t('common.resolved')}</option>
                <option value="ALL">{t('common.all')}</option>
              </select>
            </label>
            <label className="flex items-center gap-1">
              <span className="text-fg-muted">{t('incidents.filterSeverity')}</span>
              <select
                className="rounded border border-border bg-surface px-2 py-1"
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value as typeof severityFilter)}
              >
                <option value="ALL">{t('common.all')}</option>
                {SEVERITY_KEYS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {incidents.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('incidents.noIncidents')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {incidents.map((i) => (
                <li key={i.id} className="py-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <AlertTriangle
                          className={`h-4 w-4 ${
                            i.severity === 'CRITICAL'
                              ? 'text-danger'
                              : i.severity === 'HIGH'
                                ? 'text-warning'
                                : 'text-fg-muted'
                          }`}
                        />
                        <Badge
                          tone={SEVERITY_TONES[i.severity as IncidentSeverity] ?? 'neutral'}
                          className="text-[10px]"
                        >
                          {i.severity}
                        </Badge>
                        <Badge tone="neutral" className="text-[10px]">
                          {catLabel(i.category)}
                        </Badge>
                        {i.resolvedAt && (
                          <Badge tone="success" className="text-[10px] inline-flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            {t('incidents.resolvedMttr', { minutes: i.mttrMinutes ?? 0 })}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1">{i.description}</p>
                      {i.rootCause && (
                        <p className="text-xs text-fg-muted mt-1">
                          <span className="font-medium">{t('incidents.rootCause')}:</span> {i.rootCause}
                        </p>
                      )}
                      {i.mitigations && (
                        <p className="text-xs text-fg-muted mt-1">
                          <span className="font-medium">{t('incidents.mitigations')}:</span>{' '}
                          {i.mitigations}
                        </p>
                      )}
                      <p className="text-xs text-fg-muted mt-1">
                        {t('common.detectedAt')} {new Date(i.detectedAt).toLocaleString()}
                        {i.resolvedAt && (
                          <span>
                            {' · '}
                            {t('common.resolvedAt')} {new Date(i.resolvedAt).toLocaleString()}
                          </span>
                        )}
                      </p>
                    </div>
                    {!i.resolvedAt && (
                      <Button variant="ghost" onClick={() => void resolve(i)} disabled={busy}>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('actions.resolve')}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
